import { TaskCard } from "@/components/environments/TaskCard";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { Heading2 } from "@/components/podkit/typography/Headings";
import {
    useListTaskExecutions,
    useListTasks,
    type PlainTask,
    type PlainTaskExecution,
} from "@/queries/automations-queries";
import { useCallback, useMemo, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const EnvironmentTasks: FC<{ environmentId: string; disabled: boolean }> = ({ environmentId, disabled }) => {
    const navigate = useNavigate();

    const { data: tasks, error: listTasksError } = useListTasks(environmentId);
    const { data: taskRuns, error: listTaskExecutionsError } = useListTaskExecutions(environmentId);

    const hasTaskRuns = (taskRuns?.length ?? 0) > 0;

    const tasksWithTriggers = useMemo(() => {
        return (tasks || []).filter((t) => {
            // Only show tasks that have triggers and are not system/internal tasks
            const hasTriggers = t.metadata?.triggeredBy.some((t) => t.trigger);
            // Filter out tasks that might be internal/system tasks by checking if they have user-facing names
            const isUserFacingTask = t.metadata?.name && !t.metadata.name.startsWith("_");
            return hasTriggers && isUserFacingTask;
        });
    }, [tasks]);

    const onClick = useCallback(() => {
        navigate(`/details/${environmentId}/tasks`);
    }, [environmentId, navigate]);

    /** Empty state is handled in {@link EnvironmentAutomations} */
    if (tasksWithTriggers.length === 0 && !listTasksError && !listTaskExecutionsError) {
        return null;
    }

    return (
        <div className="duration-700 animate-in fade-in" data-testid="environment-tasks">
            <div className="flex items-center justify-between pb-4" translate="no">
                <Heading2 className="select-none text-xl font-bold text-content-primary" data-testid="tasks-heading">
                    Tasks
                </Heading2>
                <Button
                    disabled={disabled || !hasTaskRuns}
                    onClick={onClick}
                    variant="secondary"
                    data-testid="view-task-runs-button"
                    data-tracking-id="view-task-runs-environment-tasks"
                >
                    View Task Runs
                </Button>
            </div>
            <ErrorMessage error={listTasksError} data-testid="tasks-error" />
            <ErrorMessage error={listTaskExecutionsError} data-testid="task-executions-error" />
            <TaskCards disabled={disabled} tasks={tasksWithTriggers} runs={taskRuns || []} />
        </div>
    );
};

const TaskCards: FC<{ tasks: PlainTask[]; runs: PlainTaskExecution[]; disabled?: boolean }> = ({
    tasks,
    runs,
    disabled,
}) => {
    return (
        <div className="grid grid-cols-1 gap-4 @md:grid-cols-2 @lg:grid-cols-3">
            {tasks.map((task) => (
                <TaskCard
                    key={task.id}
                    task={task}
                    runs={runs.filter((r) => r.metadata?.taskId === task.id)}
                    disabled={disabled}
                />
            ))}
        </div>
    );
};
